import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Field, Input, Checkbox, SubmitButton, ErrorBanner, CommonIdentityFields
} from "@/components/auth/FormPrimitives";
import api, { formatApiError } from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { consumePostAuthRedirect } from "@/utils/postAuthRedirect";

export default function ClassTeacherSignupForm() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteState, setInviteState] = useState({ checking: false, valid: null, schoolName: "" });
  const [values, setValues] = useState({
    full_name: "", email: "", phone: "", password: "",
    country: "India", timezone: "Asia/Kolkata",
    invite_code: "", class_grade: "",
    accept_terms: false, accept_privacy: false,
  });
  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const validateInvite = async (code) => {
    const c = (code || "").trim().toUpperCase();
    if (c.length !== 8) {
      setInviteState({ checking: false, valid: null, schoolName: "" });
      return;
    }
    setInviteState({ checking: true, valid: null, schoolName: "" });
    try {
      const { data } = await api.get(`/api/auth/teacher-invite/${c}`);
      setInviteState({ checking: false, valid: true, schoolName: data.school_name || "" });
    } catch (e) {
      setInviteState({ checking: false, valid: false, schoolName: "" });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (inviteState.valid !== true) {
      setError("Please enter a valid invite code from your school.");
      return;
    }
    setLoading(true);
    try {
      await signup("class_teacher", { ...values, invite_code: values.invite_code.toUpperCase() });
      navigate(consumePostAuthRedirect(), { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="teacher-signup-form">
      <Field label="School invite code" required htmlFor="teacher-invite" hint="Ask your School admin for an 8-character code.">
        <Input
          id="teacher-invite"
          testid="teacher-invite"
          required
          maxLength={8}
          value={values.invite_code}
          onChange={(e) => {
            const up = e.target.value.toUpperCase();
            set("invite_code", up);
            validateInvite(up);
          }}
          placeholder="e.g. R4K9XP2M"
          className="uppercase tracking-widest"
        />
        {inviteState.checking && <p className="text-xs text-slate-400 mt-1">Validating…</p>}
        {inviteState.valid === true && (
          <p className="text-xs text-emerald-300 mt-1" data-testid="teacher-invite-ok">
            ✓ Valid code for <strong>{inviteState.schoolName}</strong>
          </p>
        )}
        {inviteState.valid === false && (
          <p className="text-xs text-rose-300 mt-1" data-testid="teacher-invite-bad">
            Invalid or already used invite code.
          </p>
        )}
      </Field>

      <CommonIdentityFields values={values} onChange={set} testidPrefix="teacher-signup" />

      <Field label="Class / Grade assignment" required htmlFor="teacher-grade">
        <Input
          id="teacher-grade"
          testid="teacher-grade"
          required
          value={values.class_grade}
          onChange={(e) => set("class_grade", e.target.value)}
          placeholder="e.g. Grade 5 - Section B"
        />
      </Field>

      <div className="pt-2 space-y-2">
        <Checkbox
          testid="teacher-accept-terms"
          checked={values.accept_terms}
          onChange={(e) => set("accept_terms", e.target.checked)}
        >
          I agree to the <a href="/terms" target="_blank" className="text-sky-400 hover:underline">Terms of Service</a>
        </Checkbox>
        <Checkbox
          testid="teacher-accept-privacy"
          checked={values.accept_privacy}
          onChange={(e) => set("accept_privacy", e.target.checked)}
        >
          I have read the <a href="/privacy" target="_blank" className="text-sky-400 hover:underline">Privacy Policy</a>
        </Checkbox>
      </div>

      <ErrorBanner error={error} />
      <SubmitButton loading={loading} testid="teacher-signup-submit">Create Class Teacher account</SubmitButton>
    </form>
  );
}
